<i18n src="./resources/locales.json"></i18n>
<template>
  <div>
    <v-expansion-panels :value="openPanel">
      <v-expansion-panel class="email-panel">
        <v-expansion-panel-header class="email-header-panel" color="surface lighten-1">
          <template v-slot="{ open }">
            <div class="d-flex flex-column">
              <EcsEmailFrom
                v-show="open && isSmtpEnabled"
                :smtp-details="smtpDetails"
                :from-email="email.outgoingEmailData.from"
                @updateFromEmail="updateFromEmail"
              />
              <EcsEmailsChipField
                label="to"
                :class="{ 'mt-2': open && isSmtpEnabled }"
                :emails="toEmailsArray"
                :error-messages="toErrorMessage"
                :disabled="disabled"
                :delimiters="[';']"
                :on-change="updateToEmails"
              />
              <EcsEmailsChipField
                v-show="open"
                class="mt-2"
                label="cc"
                :emails="ccEmailsArray"
                :error-messages="ccErrorMessage"
                :disabled="disabled"
                :delimiters="[';']"
                :on-change="updateCcEmails"
              />
              <EcsEmailsChipField
                v-show="open"
                class="mt-2"
                label="bcc"
                :emails="bccEmailsArray"
                :error-messages="bccErrorMessage"
                :disabled="disabled"
                :delimiters="[';']"
                :required-emails="requiredBccEmails"
                :on-change="updateBccEmails"
              />
              <div class="d-flex flex-row flex-nowrap mt-2">
                <v-text-field
                  dense
                  filled
                  class="subject-field"
                  background-color="surface"
                  hide-details="auto"
                  :value="email.outgoingEmailData.subject"
                  :disabled="disabled"
                  :error="subjectError"
                  :error-messages="subjectErrorMessage"
                  @input="updateSubject"
                  @click.stop
                  @keyup.space.prevent
                >
                  <template #prepend-inner>
                    {{ $t('subject') }}
                  </template>
                </v-text-field>
                <v-badge
                  :content="email.outgoingEmailData.attachments.length"
                  :value="!open && email.outgoingEmailData.attachments.length > 0"
                  color="primary"
                  overlap
                >
                  <EcsAttachFiles :interaction-id="email.interactionId" />
                </v-badge>
              </div>
              <EcsEmailUploadAttachments v-show="open" :class="{ 'mt-2': hasElements }" :email="email" />
            </div>
          </template>
          <template v-slot:actions>
            <v-icon @click.stop="togglePanel">
              mdi-chevron-down
            </v-icon>
          </template>
        </v-expansion-panel-header>
      </v-expansion-panel>
    </v-expansion-panels>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import EcsEmailFrom from '@/components/ecs-email/ecs-email-from/EcsEmailFrom';
import EcsEmailsChipField from '@/components/ecs-email/ecs-email-header/EcsEmailsChipField';
import EcsAttachFiles from '@/components/ecs-email/ecs-attach-files/EcsAttachFiles';
import EcsEmailUploadAttachments from '@/components/ecs-email/ecs-email-upload-attachments/EcsEmailUploadAttachments';

export default {
  name: 'EcsEmailHeader',

  components: { EcsEmailFrom, EcsAttachFiles, EcsEmailsChipField, EcsEmailUploadAttachments },

  props: {
    email: {
      type: Object,
      default: () => {}
    },
    disabled: {
      type: Boolean,
      default: false
    },
    businessProcessId: {
      type: String,
      required: true
    }
  },

  data() {
    return {
      openPanel: undefined
    };
  },

  computed: {
    ...mapGetters('REFERENCES', ['getSmtpDetails', 'isSmtpEnabled']),

    toErrorMessage() {
      const result = [];
      if (typeof this.email.areToEmailsValid === 'string') {
        result.push(this.$t(this.email.areToEmailsValid));
      }
      return result;
    },

    ccErrorMessage() {
      const result = [];
      if (typeof this.email.areCcEmailsValid === 'string') {
        result.push(this.$t(this.email.areCcEmailsValid));
      }
      return result;
    },

    bccErrorMessage() {
      const result = [];
      if (typeof this.email.areBccEmailsValid === 'string') {
        result.push(this.$t(this.email.areBccEmailsValid));
      }
      return result;
    },

    subjectError() {
      let result = false;
      if (typeof this.email.isSubjectValid === 'string') {
        result = true;
      }
      return result;
    },

    subjectErrorMessage() {
      const result = [];
      if (typeof this.email.isSubjectValid === 'string') {
        result.push(this.$t(this.email.isSubjectValid));
      }
      return result;
    },

    smtpDetails() {
      return this.getSmtpDetails(this.businessProcessId);
    },

    bccEmailsArray() {
      let bccEmails = [];
      if (this.email.outgoingEmailData.requiredBcc !== '') {
        bccEmails.push(this.email.outgoingEmailData.requiredBcc);
      }
      const emailBcc = this.email.outgoingEmailData.bcc.split(';').filter(e => e !== '');
      bccEmails = bccEmails.concat(emailBcc).join(';');
      return bccEmails;
    },

    toEmailsArray() {
      return this.email.outgoingEmailData.to
        .split(';')
        .filter(email => email !== '')
        .join(';');
    },

    ccEmailsArray() {
      return this.email.outgoingEmailData.cc
        .split(';')
        .filter(email => email !== '')
        .join(';');
    },

    requiredBccEmails() {
      if (!this.email.outgoingEmailData || !this.email.outgoingEmailData.requiredBcc) {
        return [];
      }
      return this.email.outgoingEmailData.requiredBcc.split(';').filter(email => email !== '');
    },

    hasElements() {
      return this.email.outgoingEmailData.attachments.length > 0;
    }
  },

  watch: {
    smtpDetails(newValue, oldValue) {
      if (newValue.overrideBCCEmailEmailAddress !== '' && oldValue.overrideBCCEmailEmailAddress === '') {
        this.$emit('updateRequiredBccEmails', newValue.overrideBCCEmailEmailAddress);
      } else if (newValue.defaultBccEmailAddress !== '' && oldValue.defaultBccEmailAddress === '') {
        this.$emit('updateRequiredBccEmails', newValue.defaultBccEmailAddress);
      }
    }
  },

  mounted() {
    if (this.smtpDetails.overrideBCCEmailAddress && this.smtpDetails.overrideBCCEmailAddress !== '') {
      this.$emit('updateRequiredBccEmails', this.smtpDetails.overrideBccEmailAddress);
    } else if (this.smtpDetails.defaultBccEmailAddress && this.smtpDetails.defaultBccEmailAddress !== '') {
      this.$emit('updateRequiredBccEmails', this.smtpDetails.defaultBccEmailAddress);
    }
  },

  methods: {
    updateCcEmails(payload = []) {
      const emails = payload.filter(email => email.trim().length > 0).join(';');
      this.$emit('updateCcEmails', emails);
    },

    updateBccEmails(payload) {
      const emails = payload
        .filter(email => this.email.outgoingEmailData.requiredBcc !== email && email.trim().length > 0)
        .join(';');

      this.$emit('updateBccEmails', emails);
    },

    updateToEmails(payload) {
      const emails = payload.filter(email => email.trim().length > 0).join(';');
      this.$emit('updateToEmails', emails);
    },

    updateSubject(value) {
      this.$emit('updateSubject', value);
    },

    updateFromEmail(payload) {
      this.$emit('updateFromEmail', payload);
    },

    togglePanel() {
      let defaultOpenPanel;
      if (this.openPanel === 0) {
        this.openPanel = defaultOpenPanel;
      } else {
        this.openPanel = 0;
      }
    }
  }
};
</script>

<style>
.email-header-panel {
  padding: 16px;
  border-top-left-radius: 0px;
  border-top-right-radius: 0px;
}
.subject-field .v-input__slot {
  min-height: 28px !important;
}
.subject-field .v-input__prepend-inner {
  margin-top: 7px !important;
}
.subject-field .v-text-field__details {
  margin-bottom: 0px !important;
}
</style>
